import React, { useState } from 'react';
import {
  Database,
  Code2,
  Globe,
  Server,
  Layout,
  Terminal,
  Github,
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const additionalProjects = [
  {
    id: 1,
    name: 'Library Management API',
    description:
      'RESTful API for managing library resources, book loans, and user accounts with comprehensive authentication and authorization.',
    tags: ['api', 'c#', 'sql'],
    icon: Database,
    githubLink: 'https://github.com/warjonas/LibraryManagementApi',
    status: 'in progress',
  },
  {
    id: 2,
    name: 'Basic Game Store API',
    description:
      'A C# ASP.NET Core Web API for managing video game details, characters, and their descriptions. Built with Entity Framework Core, this API provides a foundation for a scalable video game store backend, with potential extensions to include store details, inventory, and more.',
    tags: ['api', 'c#', 'sqllite'],
    icon: Server,
    githubLink: 'https://github.com/warjonas/VideoGameCharacterApi',
    status: 'completed',
  },
];

const AdditionalProjects = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [contentRef, contentVisible] = useScrollAnimation(0.1);
  const [selectedTag, setSelectedTag] = useState('all');

  // Get all unique tags
  const allTags = [
    'all',
    ...new Set(additionalProjects.flatMap((project) => project.tags)),
  ];

  // Filter projects based on selected tag
  const filteredProjects =
    selectedTag === 'all'
      ? additionalProjects
      : additionalProjects.filter((project) =>
          project.tags.includes(selectedTag),
        );

  return (
    <section className="py-24 px-4 md:px-8 relative bg-zinc-950/50">
      <div className="max-w-6xl mx-auto">
        <div
          ref={titleRef}
          className={`text-center mb-8 scroll-animate ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Additional Work
            </span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-3xl mx-auto">
            These are some additional projects that I have worked on and
            currently working on
          </p>
        </div>

        {/* Filter Tags */}
        <div
          ref={contentRef}
          className={`flex flex-wrap justify-center gap-3 mb-12 scroll-animate ${contentVisible ? 'visible' : ''}`}
        >
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setSelectedTag(tag)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                selectedTag === tag
                  ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white shadow-lg shadow-purple-500/20'
                  : 'bg-zinc-900/40 border border-zinc-800 text-zinc-400 hover:border-purple-500/50 hover:text-purple-300'
              }`}
            >
              {tag.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Projects List */}
        <div className="space-y-6">
          {filteredProjects.map((project, index) => {
            const IconComponent = project.icon;
            return (
              <a
                key={project.id}
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-2xl bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 hover:border-purple-500/50 transition-all duration-500 hover:shadow-xl hover:shadow-purple-500/20 p-6 mb-3 block cursor-pointer"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/0 to-cyan-500/0 group-hover:from-purple-600/5 group-hover:to-cyan-500/5 transition-all duration-500"></div>

                {/* Status Badge - Top Right */}
                <div className="absolute top-6 right-6">
                  {project.status === 'completed' ? (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 text-green-400">
                      Completed
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-amber-600/20 to-orange-600/20 border border-amber-500/30 text-amber-400">
                      In Progress
                    </span>
                  )}
                </div>

                <div className="relative flex items-start gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-cyan-500 p-3 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-full h-full text-white" />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-zinc-100 group-hover:text-purple-300 transition-colors mb-2 pr-24">
                      {project.name}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                      {project.description}
                    </p>

                    {/* Tags and GitHub Button Container */}
                    <div className="flex items-center justify-between">
                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 text-xs rounded-full bg-zinc-800/50 border border-zinc-700 text-zinc-300"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* GitHub Button - Bottom Right */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(project.githubLink, '_blank');
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-purple-500/50 hover:bg-zinc-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                      >
                        <Github className="w-4 h-4 text-zinc-400" />
                        <span className="text-xs font-medium text-zinc-300">
                          Code
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <Terminal className="w-16 h-16 mx-auto mb-4 text-zinc-700" />
            <p className="text-zinc-500 text-lg">
              No projects found with this tag
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdditionalProjects;
